import { ChangeEvent, forwardRef, memo, useCallback } from 'react';

import FormControlLabel from '@material-ui/core/FormControlLabel';
import clsx from 'clsx';

import { CheckboxCircleIcon } from '../../assets';
import { CheckboxIcon } from '../../assets/icons/CheckboxIcon';
import { CheckboxStyled, useStyles } from './Checkbox.styles';
import { CheckboxColor, CheckboxProps } from './Checkbox.types';

const getCheckboxStyles = (color?: CheckboxColor): string | undefined => {
  if (!color || color === 'default') return undefined;

  // Always use custom classes with our own palette
  return `color-${color}`;
};

export const Checkbox = memo(
  forwardRef<HTMLButtonElement, CheckboxProps>(
    (
      {
        variant = 'standard',
        color,
        label,
        value,
        onChange,
        checked,
        className,
        formControlClasses,
        name,
        'data-testid': dataTestId,
        ...props
      }: CheckboxProps,
      ref,
    ) => {
      const styles = useStyles();
      const colorClass = getCheckboxStyles(color);

      const handleChange = useCallback(
        (e: ChangeEvent<HTMLInputElement>) => {
          onChange && onChange(e.target.checked);
          e.target.blur();
        },
        [onChange],
      );

      return (
        <FormControlLabel
          label={label}
          control={
            <CheckboxStyled
              {...props}
              name={name}
              className={colorClass}
              inputProps={{
                'aria-label': label?.toString() || 'checkbox',
                ...(dataTestId ? { 'data-testid': dataTestId as string } : {}),
              }}
              icon={
                variant === 'outline' ? (
                  <CheckboxIcon size={props.size} />
                ) : variant === 'circle' ? (
                  <CheckboxCircleIcon size={props.size} />
                ) : undefined
              }
              checkedIcon={
                variant === 'outline' ? (
                  <CheckboxIcon size={props.size} checked />
                ) : variant === 'circle' ? (
                  <CheckboxCircleIcon size={props.size} checked />
                ) : undefined
              }
              value={value}
              onChange={handleChange}
              checked={checked}
              ref={ref}
            />
          }
          classes={formControlClasses}
          className={clsx(styles.label, props.size === 'small' && styles.labelSmall, className)}
        />
      );
    },
  ),
);
